import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/Auth";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import AppTheme from "../shared-theme/AppTheme";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import api from "../helpers/api";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const GradientBackground = styled("div")({
  position: "fixed",
  inset: 0,
  background: "radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)",
  zIndex: -1,
});

const SettingsContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  padding: theme.spacing(2),
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

const ManageSubscription: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<{
    status: string;
    periodStart: string;
    periodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      const sub = await api.get("/user/subscription");
      setSubscription(sub.data.subscription);
    };
    fetchSubscription();
  }, []);

  const handleCancelSubscription = async () => {
    try {
      setError(null);
      setIsLoading(true);
      await api.post("/subscription/status", { action: "cancel" });
      const sub = await api.get("/user/subscription");
      setSubscription(sub.data.subscription);
      window.location.reload();
    } catch (error: any) {
      const message =
        "There was an error cancelling your subscription. If this problem persists please contact support@event-spy.com";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenewalSubscription = async () => {
    try {
      setError(null);
      setIsLoading(true);
      await api.post("/subscription/status", { action: "renew" });
      const sub = await api.get("/user/subscription");
      setSubscription(sub.data.subscription);
      window.location.reload();
    } catch (error: any) {
      const message =
        "There was an error renewing your subscription. If this problem persists please contact support@event-spy.com";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await api.post("/subscription/checkout");
      const { url } = response.data;
      if (url) {
        window.location.href = url;
      } else {
        setError("No checkout URL received from the server");
      }
    } catch (error: any) {
      const message =
        error.response?.status === 500
          ? "Server error occurred. Please try again later."
          : "Error initiating checkout";
      setError(message);
      console.error("Error initiating checkout:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleConfirmCancel = async () => {
    setOpenDialog(false);
    await handleCancelSubscription();
  };

  const isSubscriptionExpired = (periodEnd: string | null) => {
    if (!periodEnd) return false;
    return new Date(periodEnd) < new Date();
  };

  const getNextBillingDate = (periodStart: string) => {
    const date = new Date(periodStart);
    const today = new Date();
    const nextBilling = new Date(
      today.getFullYear(),
      today.getMonth(),
      date.getDate()
    );

    if (nextBilling < today) {
      nextBilling.setMonth(nextBilling.getMonth() + 1);
    }

    return nextBilling.toLocaleDateString();
  };

  if (!user) {
    return (
      <AppTheme>
        <CssBaseline enableColorScheme />
        <GradientBackground />
        <SettingsContainer>
          <Card>
            <Typography variant="h6">Loading...</Typography>
          </Card>
        </SettingsContainer>
      </AppTheme>
    );
  }

  return (
    <AppTheme>
      <>
        {/* Meta tags for SEO */}
        <Helmet>
          <title>Manage Subscription | Event Spy</title>
        </Helmet>
      </>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <SettingsContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            Manage Subscription
          </Typography>

          {subscription &&
          subscription.status === "active" &&
          !isSubscriptionExpired(subscription.periodEnd) ? (
            <>
              <Typography variant="body1">
                {subscription.cancelAtPeriodEnd
                  ? `Your subscription will end on ${new Date(
                      subscription.periodEnd!
                    ).toLocaleDateString()}`
                  : `Your subscription is active and will renew on ${getNextBillingDate(
                      subscription.periodStart
                    )}`}
              </Typography>
              {subscription.cancelAtPeriodEnd ? (
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={handleRenewalSubscription}
                  disabled={isLoading}
                  sx={{ mt: 2 }}
                >
                  {isLoading ? "Processing..." : "Renew Subscription"}
                </Button>
              ) : (
                <Button
                  fullWidth
                  color="error"
                  variant="contained"
                  onClick={handleCancelClick}
                  disabled={isLoading}
                  sx={{ mt: 2 }}
                >
                  {isLoading ? "Processing..." : "Cancel Subscription"}
                </Button>
              )}
            </>
          ) : (
            <>
              <Typography variant="body1">
                Your subscription expired on{" "}
                {new Date(subscription?.periodEnd!).toLocaleDateString()}
              </Typography>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                onClick={handleCheckout}
                disabled={isLoading}
                sx={{ mt: 2 }}
              >
                {isLoading ? "Processing..." : "Purchase Subscription"}
              </Button>
            </>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate("/console")}
            sx={{ mt: 2 }}
          >
            Back to Console
          </Button>
        </Card>
      </SettingsContainer>
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription? You will continue
            to have access until the end of your billing period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>No, Keep It</Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default ManageSubscription;
